import { isAnyOf } from '@reduxjs/toolkit';
import { notifyBugsnag } from 'cfa-react-logging';

import authClient from '../authClient';
import ooeConstants from '../constants';
import { actions as diagnosticMessagesActions } from '../diagnosticMessages/reducer';
import { selectAllDiagnosticMessages } from '../diagnosticMessages/selectors';
import { AppStartListening } from '../listenerMiddleware';
import { State } from '../reducers';
import { logEvent, setUser, setUserLocation } from '../services/analytics';
import history from '../util/history';
import { actions as userActions } from './reducer';
import {
  doesLocationNumberExist,
  hasAllZeroLocationNumber,
  isAdminUser,
  isVcaUser,
  selectDefaultLocation,
  selectLocationNumber,
  selectLocationTimezone,
} from './selectors';
import {
  addUserToAcknowledgement,
  checkEulaMessageOnLoad,
  getEulaMessage,
  getStoreNamesAndNumbers,
  getUserLocations,
  lookupSingleLocation,
} from './thunks';
import { isTimezoneMismatched } from './utils';

const addUserListeners = (startListening: AppStartListening) => {
  startListening({
    actionCreator: userActions.updateUserInfo,
    effect: async (action, listenerApi) => {
      const { getState, dispatch } = listenerApi;
      const state = getState() as State;
      const isAdmin = isAdminUser(state);
      const isVca = isVcaUser(state);
      const hasAllZeroLocation = hasAllZeroLocationNumber(state);
      const usesVcaMiamiLocationNumbers = (isAdmin || isVca) && hasAllZeroLocation;

      let locationNumbers = usesVcaMiamiLocationNumbers
        ? ['03662', '00070']
        : action.payload.user.locationNumbers;
      if (!Array.isArray(locationNumbers)) {
        notifyBugsnag({
          grouping: 'Missing location numbers',
          message: 'Missing location numbers',
          error: { isAdmin, isVca, hasAllZeroLocation },
        });
        dispatch(userActions.throwFullScreenError());
        return;
      }

      const defaultLocation = selectDefaultLocation(state);
      if (usesVcaMiamiLocationNumbers && defaultLocation) {
        locationNumbers.unshift(defaultLocation);
        locationNumbers = [...new Set(locationNumbers)];
      }

      dispatch(getUserLocations({ locationNumbers }));
    },
  });

  startListening({
    matcher: isAnyOf(userActions.updateUserLocation, getUserLocations.fulfilled),
    effect: (_, listenerApi) => {
      const state = listenerApi.getState() as State;
      setUserLocation(selectLocationNumber(state));
    },
  });

  startListening({
    actionCreator: userActions.updateUserLocation,
    effect: (action, listenerApi) => {
      const { dispatch, getState } = listenerApi;
      const state = getState() as State;
      const { locationNumber } = action.payload;

      if (doesLocationNumberExist(state, locationNumber)) {
        return;
      }

      dispatch(lookupSingleLocation({ locationNumber }));
    },
  });

  startListening({
    matcher: isAnyOf(
      userActions.updateUserLocation,
      getUserLocations.fulfilled,
      lookupSingleLocation.fulfilled,
    ),
    effect: (_, listenerApi) => {
      const { dispatch, getState } = listenerApi;
      const state = getState() as State;
      const timeZone = selectLocationTimezone(state);
      const timezoneMismatch = ooeConstants.DIAGNOSTIC_MESSAGES.TIMEZONE_MISMATCH;
      const existingMessage = selectAllDiagnosticMessages(state).find(({ id }) => id === timezoneMismatch.id);

      if (!isTimezoneMismatched(timeZone)) {
        if (existingMessage) {
          dispatch(diagnosticMessagesActions.removeMessage({ id: timezoneMismatch.id }));
        }
        return;
      }

      if (!existingMessage) {
        dispatch(diagnosticMessagesActions.addMessage(timezoneMismatch));
      }
    },
  });

  startListening({
    actionCreator: userActions.updateUserInfo,
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(getStoreNamesAndNumbers());
    },
  });

  startListening({
    matcher: isAnyOf(checkEulaMessageOnLoad.rejected, addUserToAcknowledgement.rejected),
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(getEulaMessage());
    },
  });

  startListening({
    actionCreator: userActions.throwFullScreenError,
    effect: () => {
      history.push({ pathname: '/error' });
    },
  });

  startListening({
    actionCreator: userActions.logoutUser,
    effect: () => {
      authClient.signOut({
        clearTokensBeforeRedirect: true,
        revokeAccessToken: true,
        revokeRefreshToken: true,
      });
    },
  });

  startListening({
    actionCreator: userActions.updateUserInfo,
    effect: (action, listenerApi) => {
      const { dispatch } = listenerApi;
      const { user } = action.payload;
      const permissions = user.permissions;
      const permissionTypes = Object.keys(permissions);
      const isVca = [ooeConstants.PERMISSIONS.VCA, ooeConstants.PERMISSIONS.VCA_BYPASS].some((item) =>
        permissionTypes.includes(item),
      );
      dispatch(userActions.setBBRForVCA({ isVca }));
    },
  });

  startListening({
    actionCreator: userActions.updateUserInfo,
    effect: (action) => {
      const { user } = action.payload;

      if (user.cfaGuid) {
        setUser(user.cfaGuid);
      }

      logEvent(action.type);
    },
  });

  startListening({
    actionCreator: userActions.toggleBypassBusinessRules,
    effect: (action) => {
      logEvent(action.type);
    },
  });
};

export default addUserListeners;
